/**
 * Referred Doctor – Histopathology master for referred doctor demographics.
 * Migrated from admin4.0 DoctorDemographics (Laboratory/Config); uses HISTO_REFERRAL_DOCTOR.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Stack,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  InputAdornment,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Colors } from "../../Styles/Colors";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import { ImagePaths } from "../../Utility/ImagePaths";
import { DeletePopup } from "../../Components/Common Components/CommonComponents";
import { CheckAccessFunc } from "../../Components/Common Components/DashboardComponents";
import "../../Styles/Configuration.css";

export default function ReferredDoctor() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refDoctorList, setRefDoctorList] = useState([]);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [regNo, setRegNo] = useState("");
  const [specilization, setSpecilization] = useState("");
  const [qualification, setQualification] = useState("");
  const [clinic, setClinic] = useState("");
  const [selectedEditID, setSelectedEditID] = useState("");
  const [deletedPopup, setDeletedPopup] = useState(false);
  const [deletedID, setDeletedID] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedIndex, setSelectedIndex] = useState("");
  const [disableBtn, setDisableBtn] = useState(false);
  const [isLoader, setIsLoader] = useState(false);

  const editAccess = CheckAccessFunc("Histopathology", "Settings", "Referred Doctor", null, "Tab")?.editAccess;

  const msgClose = useCallback(() => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setSuccessMsg(false);
    setSuccessMsgText("");
  }, []);

  const getRefDoctorList = useCallback((searchKey) => {
    setIsLoader(true);
    const url = searchKey
      ? `${Serviceurls.HISTO_REFERRAL_DOCTOR}?search_key=${encodeURIComponent(searchKey)}`
      : Serviceurls.HISTO_REFERRAL_DOCTOR;
    RestAPIService.getAll(url)
      .then((response) => {
        if (response?.data?.status === "success") {
          setRefDoctorList(response.data.data ?? []);
        }
        setIsLoader(false);
      })
      .catch((e) => {
        setIsLoader(false);
        setIsErrorMsg(true);
        setIsErrorMsgText(e?.response?.data?.message || e?.message);
      });
  }, []);

  useEffect(() => {
    getRefDoctorList();
  }, [getRefDoctorList]);

  const postRefDoctorList = useCallback(() => {
    const data = {
      name: (doctorName || "").replace(/\s+/g, " ").trim(),
      mobile_number: mobileNo || "",
      reg_no: (regNo || "").toString().trim(),
      qualification: (qualification || "").replace(/\s+/g, " ").trim(),
      specialization: (specilization || "").replace(/\s+/g, " ").trim(),
      hospital_name: (clinic || "").replace(/\s+/g, " ").trim(),
    };
    if (selectedEditID) data.id = selectedEditID;
    setDisableBtn(true);
    RestAPIService.create(data, Serviceurls.HISTO_REFERRAL_DOCTOR)
      .then((response) => {
        if (response?.data?.status === "success") {
          setSuccessMsg(true);
          setSuccessMsgText(response.data.message);
          getRefDoctorList();
          clearValue();
        }
        setDisableBtn(false);
      })
      .catch((e) => {
        setIsErrorMsg(true);
        setIsErrorMsgText(e?.response?.data?.message || e?.message);
        setDisableBtn(false);
      });
  }, [doctorName, mobileNo, regNo, qualification, specilization, clinic, selectedEditID, getRefDoctorList]);

  const editRegDoctorList = (item, index) => {
    setSelectedEditID(item.id);
    setDoctorName(item.name || "");
    setRegNo(item.reg_no || "");
    setMobileNo(item.mobile_number || "");
    setSpecilization(item.specialization || "");
    setQualification(item.qualification || "");
    setClinic(item.hospital_name || "");
    setSelectedIndex(index);
  };

  const deleteRefDoctorList = (ID) => {
    const urlWithId = `${Serviceurls.HISTO_REFERRAL_DOCTOR}?id=${ID}`;
    RestAPIService.delete(urlWithId)
      .then((response) => {
        if (response?.data?.status === "success") {
          setSuccessMsg(true);
          setSuccessMsgText(response.data.message);
          getRefDoctorList();
        }
      })
      .catch((e) => {
        setIsErrorMsg(true);
        setIsErrorMsgText(e?.response?.data?.message || e?.message);
      });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const clearValue = () => {
    setDoctorName("");
    setMobileNo("");
    setRegNo("");
    setSpecilization("");
    setQualification("");
    setClinic("");
    setSelectedEditID("");
    setSelectedIndex("");
  };

  const validCheck = () => {
    if (!doctorName || !mobileNo || !regNo || !specilization || !qualification || !clinic) {
      return "Please fill all mandatory fields.";
    }
    return null;
  };

  const onSave = () => {
    const invalid = validCheck();
    if (invalid) {
      setIsErrorMsg(true);
      setIsErrorMsgText(invalid);
    } else {
      postRefDoctorList();
    }
  };

  const removeDataList = () => {
    deleteRefDoctorList(deletedID);
    setDeletedPopup(false);
  };

  const filteredData = (refDoctorList || []).filter((data) => {
    const s = (searchText || "").toLowerCase();
    return (
      (data.name || "").toLowerCase().includes(s) ||
      (data.specialization || "").toLowerCase().includes(s) ||
      (data.qualification || "").toLowerCase().includes(s) ||
      (data.hospital_name || "").toLowerCase().includes(s)
    );
  });

  const textBox = (label, width, value, name) => {
    const maxLen = name === "mobileNo" ? 10 : name === "regNo" ? 7 : name === "doctorName" ? 50 : 150;
    const handleChange = (e) => {
      const v = e.target.value;
      if (maxLen && v.length > maxLen) return;
      if ((name === "mobileNo" || name === "regNo") && v !== "" && !/^[0-9+]+$/.test(v)) return;
      if ((name === "doctorName" || name === "specilization") && v !== "" && !/^[A-Za-z\s]+$/.test(v)) return;
      switch (name) {
        case "doctorName": setDoctorName(v); break;
        case "mobileNo": setMobileNo(v); break;
        case "regNo": setRegNo(v); break;
        case "specilization": setSpecilization(v); break;
        case "qualification": setQualification(v); break;
        case "clinic": setClinic(v); break;
        default: break;
      }
    };
    return (
      <TextField
        size="small"
        sx={{ width }}
        label={label + "*"}
        name={name}
        value={value}
        inputProps={{ maxLength: maxLen }}
        InputProps={name === "mobileNo" ? { startAdornment: <InputAdornment position="start">+91</InputAdornment> } : undefined}
        variant="outlined"
        onChange={handleChange}
      />
    );
  };

  const slice = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="eMed_TestConfig_MainContainer">
      <div className="eMed_TestConfig_Con">
        <div className="eMed_TestConfig_leftSide">
          <Paper elevation={0}>
            <Box className="eMed_Search_Box_">
              <TextField
                variant="standard"
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "15vw" }}
              />
            </Box>
            <TableContainer className="eMed_ConfigTable_Paper">
              <Table stickyHeader size="small">
                <TableHead sx={{ height: "7.3vh" }}>
                  <TableRow>
                    <TableCell id="eMed_config_TableRow1" align="left" sx={{ color: Colors.HeadText }}>S.No</TableCell>
                    <TableCell id="eMed_config_TableRow" align="left" sx={{ color: Colors.HeadText }}>Doctor Name</TableCell>
                    <TableCell id="eMed_config_TableRow" align="left" sx={{ color: Colors.HeadText }}>Specialization</TableCell>
                    <TableCell id="eMed_config_TableRow" align="left" sx={{ color: Colors.HeadText }}>Qualification</TableCell>
                    <TableCell id="eMed_config_TableRow" align="left" sx={{ color: Colors.HeadText }}>Hospital/Clinic Name</TableCell>
                    <TableCell id="eMed_config_TableRow" align="center" sx={{ color: Colors.HeadText }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                {isLoader ? (
                  <Box sx={{ display: "flex", justifyContent: "center", padding: "2vw" }}>
                    <span>Loading...</span>
                  </Box>
                ) : (
                  slice.map((data, index) => {
                    const sno = page * rowsPerPage + index + 1;
                    return (
                      <TableBody key={data.id || index}>
                        <TableRow sx={{ bgcolor: selectedEditID && selectedIndex === index ? "#e2e2e2" : "white" }}>
                          <TableCell align="left">{sno}</TableCell>
                          <TableCell align="left">
                            {(data.name || "").length > 15 ? (
                              <Tooltip title={data.name} placement="top" arrow>
                                <div>{data.name ? data.name.slice(0, 15) + "..." : "-"}</div>
                              </Tooltip>
                            ) : (
                              <div>{data.name || "-"}</div>
                            )}
                          </TableCell>
                          <TableCell align="left">
                            {(data.specialization || "").length > 15 ? (
                              <Tooltip title={data.specialization} placement="top" arrow>
                                <div>{data.specialization ? data.specialization.slice(0, 15) + "..." : "-"}</div>
                              </Tooltip>
                            ) : (
                              <div>{data.specialization || "-"}</div>
                            )}
                          </TableCell>
                          <TableCell align="left">
                            {(data.qualification || "").length > 15 ? (
                              <Tooltip title={data.qualification} placement="top" arrow>
                                <div>{data.qualification ? data.qualification.slice(0, 15) + "..." : "-"}</div>
                              </Tooltip>
                            ) : (
                              <div>{data.qualification || "-"}</div>
                            )}
                          </TableCell>
                          <TableCell align="left">
                            {(data.hospital_name || "").length > 15 ? (
                              <Tooltip title={data.hospital_name} placement="top" arrow>
                                <div>{data.hospital_name ? data.hospital_name.slice(0, 15) + "..." : "-"}</div>
                              </Tooltip>
                            ) : (
                              <div>{data.hospital_name || "-"}</div>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <div className="eMed_TestConfig_Con">
                              <IconButton size="small" disabled={!editAccess}>
                                <img
                                  src={editAccess ? (ImagePaths.LabEditButton?.default ?? ImagePaths.LabEditButton) : (ImagePaths.DisabledEdit?.default ?? ImagePaths.DisabledEdit)}
                                  className="eMed_TestConfig_DeleteIcon"
                                  alt="Edit"
                                  onClick={() => editRegDoctorList(data, index)}
                                />
                              </IconButton>
                              <IconButton size="small" disabled={!editAccess}>
                                <img
                                  src={editAccess ? (ImagePaths.Delete?.default ?? ImagePaths.Delete) : (ImagePaths.DisabledDelete?.default ?? ImagePaths.DisabledDelete)}
                                  className="eMed_TestConfig_DeleteIcon"
                                  alt="Delete"
                                  onClick={() => {
                                    setDeletedPopup(true);
                                    setDeletedID(data.id);
                                  }}
                                />
                              </IconButton>
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    );
                  })
                )}
              </Table>
              {filteredData.length === 0 && !isLoader ? (
                <div className="eMed_TestConfig_NoRecords">No Records Found</div>
              ) : null}
            </TableContainer>
          </Paper>
          <div className="eMed_TestConfig_LeftPageination">
            <TablePagination
              component="div"
              id="eMed_tbl_pagination"
              rowsPerPageOptions={[10, 20, 30]}
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </div>
        <div className="eMed_TestConfig_RightSide">
          <div className="eMed_TestConfig_RightHead">
            <p className="eMed_Testconfig_RightHead_text">Doctor Demographics</p>
          </div>
          <div className="eMed_TestConfig_RightContentBox">
            <div className="eMed_TestConfig_RightMethod">
              {textBox("Doctor Name", "29vw", doctorName, "doctorName")}
            </div>
            <div className="eMed_TestConfig_RightMethod">
              {textBox("Mobile No", "14vw", mobileNo, "mobileNo")}
              {textBox("Registration No", "14vw", regNo, "regNo")}
            </div>
            <div className="eMed_TestConfig_RightMethod">
              {textBox("Specialization", "18vw", specilization, "specilization")}
              {textBox("Qualification", "10vw", qualification, "qualification")}
            </div>
            <div className="eMed_TestConfig_RightMethod">
              {textBox("Hospital/Clinic Name", "29vw", clinic, "clinic")}
            </div>
          </div>
          <div className="eMed_TestConfig_RightBtnCon">
            <Stack spacing={2} direction="row">
              <Button id="eMed_Btn_Text" variant="outlined" onClick={clearValue}>Clear</Button>
              <Button id="eMed_Btn_Text" variant="contained" disabled={disableBtn || !editAccess} onClick={onSave}>Save</Button>
            </Stack>
          </div>
        </div>
      </div>
      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      {deletedPopup && (
        <DeletePopup
          DeletTitle="Are you sure you want to remove this doctor?"
          DeleteNotify="It will affect entire billing."
          deleteAlertPopupClose={() => setDeletedPopup(false)}
          removeData={removeDataList}
        />
      )}
    </div>
  );
}
